import {
  BasicFormItem,
  Column,
  FormDatepicker,
  FormInputField,
  Loader,
  Row,
  SignatureCanvas,
  Typography,
} from '@/components'
import { ThemeColor } from '@/enums'
import { ShieldCheck, SureinTrustLogo } from '@/icons'
import { useSegment } from '@/modules'
import { StyledRow } from '@/pages/CheckoutPage/styles'
import { StyledLinkTypography } from '@/pages/SelfAssessmentPage/steps/styles'
import { FormProvider, useContactModal, useFormContext } from '@/providers'
import {
  useCreatePublicCheckoutPoa,
  useDoPublicCheckout,
  usePublicCheckoutPoa,
} from '@/services'
import { theme } from '@/theme'
import { clearLocalStorage, pushEventToDataLayer, themeColor } from '@/utils'
import { Alert } from '@surein/ui'
import { InfoIcon, LockKeyhole } from 'lucide-react'
import { applySpec, join, pipe } from 'ramda'
import { useEffect, ReactNode } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { makePoaValidationSchema } from './validationSchema'

const StyledSSL = styled(Row)`
  width: fit-content;
  padding: 8px;
  background: ${themeColor(ThemeColor.success4)};
  border: 1px solid ${themeColor(ThemeColor.success3)};
  border-radius: 8px;
  margin-bottom: 4px;
`

const StyledDSGVO = styled(Row)`
  width: fit-content;
  padding: 8px;
  background: ${themeColor(ThemeColor.info3)};
  border: 1px solid ${themeColor(ThemeColor.info1)};
  border-radius: 8px;
  margin-bottom: 4px;
`

const StyledLink = styled(StyledLinkTypography)`
  font-weight: 700;
`

const StyledSureinTrustLogo = styled(SureinTrustLogo)`
  width: 32px;
  height: 38px;
`

const NumberedListCounter = styled(Typography).attrs({
  variant: 'description',
  color: ThemeColor.info1,
})`
  border-radius: 100px;
  flex-shrink: 0;
  background: ${themeColor(ThemeColor.info4)};
  height: 24px;
  width: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
`

interface NumberedListItemProps {
  counter: string | number
  children: ReactNode
}

const NumberedListItem = ({ counter, children }: NumberedListItemProps) => {
  return (
    <Row gap="16px" alignItems="center">
      <NumberedListCounter>{counter}</NumberedListCounter>
      <Typography variant="description">{children}</Typography>
    </Row>
  )
}

const useMessages = () => {
  const { t } = useTranslation()

  return {
    name: t('name'),
    birthdate: t('birthday'),
    next: t('next'),
    complete: t('complete'),
    poaAdvisorBubbleText: t('poaAdvisorBubbleText'),
    signPoaErrorHint: t('signPoaErrorHint'),
    general: t('validations.general'),
    over18: t('validations.over18'),
  }
}

interface ButtonState {
  onClick?: () => void
  text?: string
  loading?: boolean
}

interface SignPoaFormProps {
  setButtonState: (state: ButtonState) => void
}

interface FormValues {
  signature?: string
  fullname?: string
  birthdate?: string | number | null
}

const SignPoaForm = ({ setButtonState }: SignPoaFormProps) => {
  const { t } = useTranslation()
  const { errors, submitForm, customValidate } = useFormContext<FormValues>()
  const messages = useMessages()
  const { openContact } = useContactModal()
  const analytics = useSegment()

  const handleSubmit = async (): Promise<void> => {
    submitForm()
    await analytics.track('ssf_checkout_completed')
  }

  useEffect(() => {
    setButtonState({
      onClick: () =>
        customValidate({
          onSuccess: handleSubmit,
        }),
      text: messages.complete,
    })

    return () => {
      setButtonState({
        text: messages.next,
      })
    }
  }, [])

  return (
    <Column gap="16px">
      {errors.signature && (
        <Alert
          icon={InfoIcon}
          iconAlignment="top"
          // @ts-expect-error fix type in toolkit
          text={
            <Trans
              components={{
                styledLink: (
                  <StyledLink onClick={openContact} variant="captionB" />
                ),
              }}
              i18nKey={messages.signPoaErrorHint}
              variant="danger"
            />
          }
          variant="danger"
        />
      )}
      <Column gap="8px">
        <NumberedListItem counter="1">
          {t('checkout.poa.info1')}
        </NumberedListItem>
        <NumberedListItem counter="2">
          {t('checkout.poa.info2')}
        </NumberedListItem>
        <NumberedListItem counter="3">
          {t('checkout.poa.info3')}
        </NumberedListItem>
      </Column>
      <Row gap="16px" alignItems="center">
        <StyledSSL alignItems="center" gap="4px">
          <ShieldCheck color={ThemeColor.b0} />
          <Typography singleLine variant="micro">
            <Trans i18nKey="ssl" />
          </Typography>
        </StyledSSL>
        <StyledSureinTrustLogo />
        <StyledDSGVO alignItems="center" gap="4px">
          <LockKeyhole size={16} color={theme.color.info1} />
          <Typography singleLine variant="micro">
            <Trans i18nKey="gdprCompliant" />
          </Typography>
        </StyledDSGVO>
      </Row>
      <Column gap="4px">
        <BasicFormItem
          component={SignatureCanvas}
          name="signature"
          showErrorHelperText={false}
          noMargin
        />
        <StyledRow>
          <FormInputField
            label={messages.name}
            name="fullname"
            showValidCheck
          />
          <FormDatepicker
            label={messages.birthdate}
            max={new Date()}
            name="birthdate"
            showCalendar={false}
          />
        </StyledRow>
      </Column>
    </Column>
  )
}

interface UserData {
  name?: string
  surname?: string
  birthdate?: string
}

interface InitialValues {
  fullname: string
  birthdate: number | null
}

export const makeInitialValues = pipe(
  applySpec<InitialValues>({
    fullname: pipe(
      (data: UserData) => [data.name || '', data.surname || ''] as string[],
      (values: string[]) => join(' ', values.filter(Boolean)),
    ),
    birthdate: pipe(
      (data: UserData) => [data.birthdate] as (string | undefined)[],
      (values: (string | undefined)[]) => {
        const birthday = values[0]
        return birthday ? new Date(birthday).getTime() : null
      },
    ),
  }),
)

interface PublicSignPoaStepProps {
  setButtonState: (state: ButtonState) => void
  openContact: () => void
  funnelProduct: string
  openSuccess: (downloadUrl?: string) => void
}

interface PoaSubmitData {
  birthdate: string | number | null
  signature: string
}

export const PublicSignPoaStep = ({
  setButtonState,
  openContact,
  funnelProduct,
  openSuccess,
}: PublicSignPoaStepProps) => {
  const { data, isLoading } = usePublicCheckoutPoa()
  const analytics = useSegment()
  const messages = useMessages()

  const doCheckoutMutation = useDoPublicCheckout()
  const { mutateAsync, isLoading: poaIsLoading } = useCreatePublicCheckoutPoa()

  const handleError = (): void => {
    openContact()
  }

  const handleSubmit = async ({
    birthdate,
    signature,
  }: PoaSubmitData): Promise<void> => {
    try {
      await mutateAsync({ birthdate, signature })
      const { consultationProtocolUrl } = await doCheckoutMutation.mutateAsync(
        null,
      )

      pushEventToDataLayer('Step Change', {
        funnel_product: funnelProduct,
        funnel_step: 'funnel_finished',
      })

      await analytics.track('ssf_poa_signed')

      clearLocalStorage()
      openSuccess(consultationProtocolUrl)
    } catch (_error) {
      handleError()
    }
  }

  useEffect(() => {
    setButtonState({
      loading: poaIsLoading || doCheckoutMutation.isLoading,
    })
  }, [poaIsLoading, doCheckoutMutation.isLoading])

  if (isLoading) return <Loader />

  return (
    <FormProvider
      initialValues={
        data
          ? makeInitialValues(data as UserData)
          : { fullname: '', birthdate: null }
      }
      onSubmit={handleSubmit}
      validationSchema={makePoaValidationSchema(messages)}
    >
      <SignPoaForm setButtonState={setButtonState} />
    </FormProvider>
  )
}
